// src/pages/ArchetypeSelect.tsx

import { useState, useCallback, useMemo, useEffect } from "react";
import { ArchetypeId, ARCHETYPES } from "../constants/archetypes";
import ArchetypeCard from "../components/card/ArchetypeCard";
import { useResponsive } from "../hooks/useResponsive";
import { tiktokPixelService } from "../services/tiktok-pixel.service";
import { useCampaign } from "../campaigns/providers/CampaignProvider";

const DEFAULT_ARCHETYPE_IDS: ArchetypeId[] = ["heartbeat", "safehaven", "moments"];

const ARCHETYPE_THEMES = {
  moments: {
    primary: "#f9ca24",
    secondary: "#f6e58d",
    accent: "#f0932b",
    glow: "bg-yellow-400/50",
    bg: "bg-yellow-500/[0.05]",
    activeBg: "bg-yellow-600/[0.12]",
    border: "border-yellow-500/40",
    gradient: "from-yellow-500/10",
  },
  heartbeat: {
    primary: "#ff4d6d",
    secondary: "#ff8fa3",
    accent: "#ffb3c6",
    glow: "bg-red-400/50",
    bg: "bg-red-500/[0.05]",
    activeBg: "bg-red-600/[0.12]",
    border: "border-red-500/40",
    gradient: "from-red-500/10",
  },
  safehaven: {
    primary: "#a5b1c2",
    secondary: "#778ca3",
    accent: "#4b6584",
    glow: "bg-blue-400/50",
    bg: "bg-blue-500/[0.05]",
    activeBg: "bg-blue-600/[0.12]",
    border: "border-blue-500/40",
    gradient: "from-blue-500/10",
  },
};

export default function ArchetypeSelect() {
  const [activeIndex, setActiveIndex] = useState(0);
  const { isMobile } = useResponsive();
  const { activeCampaign } = useCampaign();

  const archetypeIds = useMemo<ArchetypeId[]>(() => {
    if (!activeCampaign) return DEFAULT_ARCHETYPE_IDS;

    return [
      activeCampaign.archetype,
      ...DEFAULT_ARCHETYPE_IDS.filter((id) => id !== activeCampaign.archetype),
    ];
  }, [activeCampaign]);

  useEffect(() => {
    setActiveIndex(0);
  }, [activeCampaign?.id]);

  const handleSetActive = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const getXOffset = useCallback(
    (index: number): number => {
      if (isMobile) return 0;
      if (activeIndex > index) return index * 20 - 40;
      return index * 20;
    },
    [activeIndex, isMobile]
  );

  const cards = useMemo(
    () =>
      archetypeIds.map((arch, index) => (
        <ArchetypeCard
          key={`${activeCampaign?.id || "default"}-${arch}`}
          arch={arch}
          config={ARCHETYPES[arch]}
          theme={ARCHETYPE_THEMES[arch]}
          index={index}
          activeIndex={activeIndex}
          setActive={() => handleSetActive(index)}
          xOffset={getXOffset(index)}
          isMobile={isMobile}
        />
      )),
    [activeCampaign?.id, activeIndex, archetypeIds, handleSetActive, getXOffset, isMobile]
  );

  useEffect(() => {
    tiktokPixelService.trackViewContent(
      {
        content_id: activeCampaign?.id || "archetype_select",
        content_type: "product_group",
        content_name: activeCampaign
          ? `${activeCampaign.name} Archetype Select`
          : "LRNSY Archetype Select",
      },
      0,
      "KES",
      activeCampaign
        ? `viewcontent:campaign_archetype_select:${activeCampaign.id}`
        : "viewcontent:archetype_select"
    );
  }, [activeCampaign]);

  return (
    <div className="relative h-screen w-full bg-black text-white overflow-hidden font-sans">
      {activeCampaign && (
        <div className="absolute left-1/2 top-6 z-[200] -translate-x-1/2 rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 backdrop-blur-md">
          <p
            className="text-[8px] font-bold uppercase tracking-[0.32em]"
            style={{ color: activeCampaign.theme.primary }}
          >
            {activeCampaign.name}
          </p>
        </div>
      )}

      <div className="relative w-full h-full flex items-center overflow-hidden">{cards}</div>

      <footer className="absolute bottom-0 left-0 w-full z-[100] p-8 flex justify-between items-center opacity-30 pointer-events-none">
        <div className="text-[9px] font-bold uppercase tracking-[0.3em]">LRNSY ©2026</div>
      </footer>
    </div>
  );
}
